import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';

const GOLD = '#C9A84C';

const sectionStyle = {
  borderTop: '1px solid rgba(255,255,255,0.06)',
  padding: 'clamp(4rem,8vw,6rem) clamp(1.25rem,5vw,4rem)',
};

const cardStyle = {
  borderRadius: 18,
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.04)',
  backdropFilter: 'blur(10px)',
  padding: 'clamp(1.5rem,2.5vw,2rem)',
  transition: 'border-color 250ms ease, transform 250ms ease, box-shadow 250ms ease',
};

function SectionHeading({ label, title, titleSpan, description }) {
  return (
    <div style={{ marginBottom: 'clamp(2rem,4vw,3rem)' }}>
      {label && (
        <div style={{
          display: 'inline-block', fontSize: '0.72rem', fontWeight: 700,
          color: GOLD, letterSpacing: '0.14em', textTransform: 'uppercase',
          background: `${GOLD}14`, border: `1px solid ${GOLD}30`,
          borderRadius: 9999, padding: '0.3rem 1rem', marginBottom: '1rem',
        }}>{label}</div>
      )}
      <h2 style={{ fontWeight: 900, fontSize: 'clamp(1.6rem,3vw,2.2rem)', color: '#fff', margin: '0 0 1rem', lineHeight: 1.2 }}>
        {title}{titleSpan && <> <span style={{ color: GOLD }}>{titleSpan}</span></>}
      </h2>
      {description && <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: 680, lineHeight: 1.7, margin: 0 }}>{description}</p>}
    </div>
  );
}

const hostingServices = [
  {
    icon: '🌐',
    title: 'Domain Registration',
    description: 'Secure your brand\'s online identity with the perfect domain name — with full WHOIS privacy protection.',
    features: [
      'Domain name search & registration (.com, .ca, .net & more)',
      'Domain transfer & renewal management',
      'Private registration & WHOIS identity protection',
    ],
  },
  {
    icon: '🖥️',
    title: 'Website Hosting',
    description: 'Fast, secure, fully managed hosting built for performance — from startups to enterprise.',
    features: [
      'Shared, VPS & Dedicated Hosting options',
      'Optimized for WordPress, eCommerce & custom apps',
      'Automatic daily backups & 24/7 technical support',
    ],
  },
  {
    icon: '📧',
    title: 'Business Email Hosting',
    description: 'Professional @yourdomain.com email addresses that build credibility from the first send.',
    features: [
      'Custom business email addresses at your domain',
      'Advanced spam protection & security filters',
      'Sync with Google Workspace, Outlook & mobile devices',
    ],
  },
  {
    icon: '🔒',
    title: 'Security & SSL Certificates',
    description: 'Enterprise-level security to protect your site, data, and customers from threats.',
    features: [
      'SSL certificates for HTTPS & end-to-end encryption',
      'Firewall protection & daily malware scanning',
      'DDoS prevention & proactive vulnerability monitoring',
    ],
  },
];

const brandingServices = [
  {
    icon: '🎯',
    title: 'Brand Identity Development',
    description: 'A cohesive brand identity that tells your story, earns trust, and stays memorable across every touchpoint.',
    features: [
      'Brand voice, personality & value definition',
      'Color palette, typography & style guidelines',
      'Full brand guide for consistent multi-channel use',
    ],
  },
  {
    icon: '✏️',
    title: 'Logo Design',
    description: 'A timeless, versatile logo that anchors your visual identity — designed from scratch, not templates.',
    features: [
      'Multiple custom concepts to choose from',
      'Web, print & merchandise variations included',
      'Delivered in all formats: JPEG, PNG, SVG, AI & PDF',
    ],
  },
  {
    icon: '🖨️',
    title: 'Print & Marketing Collateral',
    description: 'Professional print materials that make your brand tangible and leave a lasting impression.',
    features: [
      'Business cards, brochures & flyers',
      'Letterheads, envelopes & branded templates',
      'Product packaging, labels & banner designs',
    ],
  },
  {
    icon: '🚀',
    title: 'Brand Launch & Rebranding',
    description: 'Whether you\'re launching fresh or refreshing your look, we ensure a smooth, impactful rollout.',
    features: [
      'Brand launch strategy & marketing plan',
      'Press release & PR campaign assets',
      'Social media & advertising creative for launch',
    ],
  },
];

const processSteps = [
  {
    step: 1,
    title: 'Discovery & Strategy',
    description: 'We learn your business, audience, and goals — then build a brand and hosting strategy around them.',
  },
  {
    step: 2,
    title: 'Visual Identity & Design',
    description: 'Logo, colour palette, typography, and brand assets designed to reflect who you are and who you serve.',
  },
  {
    step: 3,
    title: 'Hosting Infrastructure Setup',
    description: 'Domain registration, server configuration, SSL, email hosting, and security — all configured and tested.',
  },
  {
    step: 4,
    title: 'Brand Rollout & Launch',
    description: 'We apply your new brand across all digital and print touchpoints, then coordinate your public launch.',
  },
  {
    step: 5,
    title: 'Ongoing Support & Maintenance',
    description: 'Hosting health monitoring, renewals, brand asset updates, and technical support — we\'re here long term.',
  },
];

const caseStudies = [
  {
    client: 'Tech Startup',
    title: 'Full Brand Identity + Hosting for SaaS Launch',
    results: [
      'Logo, brand guide, and full collateral suite delivered in 3 weeks.',
      'Hosting stack configured with 99.9% uptime from day one.',
      'Brand consistency rated 9.4/10 by user testing panel at launch.',
    ],
    cta: 'Build your brand from the ground up',
  },
  {
    client: 'Local Retailer',
    title: 'Rebranding Campaign That Doubled Foot Traffic',
    results: [
      'Complete rebrand — new logo, print materials, and storefront signage.',
      'New website hosting migrated with 10× faster load times.',
      'Foot traffic and online inquiries both doubled in 60 days post-launch.',
    ],
    cta: 'Refresh your brand and grow',
  },
  {
    client: 'eCommerce Brand',
    title: 'Secure Hosting Setup + Product Packaging Design',
    results: [
      'SSL, firewall, and daily backup system implemented within 48 hours.',
      'Custom product labels and packaging design increased repeat purchases 28%.',
      'Business email setup cut customer-facing response time in half.',
    ],
    cta: 'Make your brand look as good as your product',
  },
];

const faqs = [
  {
    question: 'Do you offer hosting and branding as a package?',
    answer: 'Yes — we offer complete Branding & Hosting packages that cover everything from domain registration and SSL setup to logo design and brand guidelines. This gives you one team to handle it all.',
  },
  {
    question: 'How long does branding take?',
    answer: 'A complete brand identity (logo, colours, typography, brand guide) typically takes 2–4 weeks depending on scope. Faster turnarounds are available for urgent projects.',
  },
  {
    question: 'What type of hosting do you recommend?',
    answer: 'We recommend VPS or Managed Hosting for most growing businesses — it balances performance, security, and cost. We will advise you on the best fit based on your traffic, tech stack, and budget.',
  },
  {
    question: 'Will you manage my domain and hosting renewals?',
    answer: 'Yes — we can manage your domain renewal, hosting billing, SSL renewal, and security updates on your behalf so nothing lapses without notice.',
  },
  {
    question: 'Can you migrate my existing website to new hosting?',
    answer: 'Absolutely. We handle full site migrations with zero downtime, including database transfer, DNS propagation, and post-migration testing.',
  },
];

function Branding() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={{ background: '#080808', minHeight: '100vh', color: '#fff' }}>
      <Header />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section style={{
        padding: 'clamp(5rem,10vw,8rem) clamp(1.25rem,5vw,4rem) clamp(3rem,6vw,5rem)',
        maxWidth: 1100, margin: '0 auto',
      }}>
        {/* Breadcrumb */}
        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginBottom: '1.5rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
          <span>/</span>
          <Link to="/services" style={{ color: 'inherit', textDecoration: 'none' }}>Services</Link>
          <span>/</span>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>Branding &amp; Hosting</span>
        </div>

        {/* Badge */}
        <div style={{
          display: 'inline-block', fontSize: '0.72rem', fontWeight: 700,
          color: GOLD, letterSpacing: '0.14em', textTransform: 'uppercase',
          border: `1px solid ${GOLD}50`, borderRadius: 9999,
          padding: '0.35rem 1.1rem', marginBottom: '1.5rem',
        }}>
          Branding &amp; Hosting
        </div>

        {/* Title */}
        <h1 style={{ fontWeight: 900, fontSize: 'clamp(2.4rem,5vw,4rem)', lineHeight: 1.1, margin: '0 0 1rem', maxWidth: 800 }}>
          Branding &amp; Hosting
        </h1>
        <p style={{ color: GOLD, fontSize: 'clamp(1.1rem,2vw,1.4rem)', fontWeight: 600, margin: '0 0 1.25rem' }}>
          Build a Brand Worth Remembering — Then Host It Where It Will Never Go Down
        </p>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: 680, lineHeight: 1.7, margin: '0 0 2.5rem' }}>
          QuickGrowth delivers end-to-end branding and hosting services — from logo design and brand identity to domain registration, managed hosting, business email, and SSL security.
        </p>

        {/* CTA Buttons */}
        <div className="bh-cta-row" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/contact-us" className="bh-cta-btn" style={{
            background: GOLD, color: '#000', fontWeight: 700, padding: '0.85rem 2rem',
            borderRadius: 9999, textDecoration: 'none', fontSize: '0.95rem',
          }}>
            Get Started Today →
          </Link>
          <Link to="/services" className="bh-cta-btn" style={{
            border: `1px solid rgba(255,255,255,0.2)`, color: '#fff', fontWeight: 600,
            padding: '0.85rem 2rem', borderRadius: 9999, textDecoration: 'none', fontSize: '0.95rem',
          }}>
            View All Services
          </Link>
        </div>

        {/* Stats Bar */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '2rem',
          marginTop: '3rem', paddingTop: '2rem',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          {[
            { value: '500+', label: 'Brands Created' },
            { value: '99.9%', label: 'Hosting Uptime' },
            { value: '48h', label: 'Avg. Domain Setup Time' },
          ].map((s) => (
            <div key={s.label}>
              <div style={{ fontSize: 'clamp(1.4rem,2.5vw,1.8rem)', fontWeight: 900, color: GOLD }}>{s.value}</div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Hosting Services ─────────────────────────────────────── */}
      <section style={{ ...sectionStyle, background: 'rgba(255,255,255,0.015)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            label="Hosting"
            title="Everything You Need to"
            titleSpan="Stay Online & Stay Secure"
            description="From domain registration to enterprise-grade SSL security, we set up and manage your entire hosting infrastructure."
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'clamp(1rem,2vw,1.5rem)' }}>
            {hostingServices.map((s) => (
              <div key={s.title} style={{ ...cardStyle }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{s.icon}</div>
                <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', margin: '0 0 0.5rem' }}>{s.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 1rem' }}>{s.description}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {s.features.map((f) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>
                      <span style={{ color: GOLD, fontWeight: 900, flexShrink: 0 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Branding Services ────────────────────────────────────── */}
      <section style={sectionStyle}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            label="Branding"
            title="A Brand That Works as Hard"
            titleSpan="as You Do"
            description="We craft brands that connect — from the first logo concept to the full brand guide, launch strategy, and print collateral."
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'clamp(1rem,2vw,1.5rem)' }}>
            {brandingServices.map((s) => (
              <div key={s.title} style={{ ...cardStyle }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{s.icon}</div>
                <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', margin: '0 0 0.5rem' }}>{s.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 1rem' }}>{s.description}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {s.features.map((f) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>
                      <span style={{ color: GOLD, fontWeight: 900, flexShrink: 0 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ──────────────────────────────────────────────── */}
      <section style={{ ...sectionStyle, background: 'rgba(255,255,255,0.015)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            label="How It Works"
            title="Our Branding & Hosting"
            titleSpan="Process"
            description="A clear, end-to-end process from discovery through to live brand and running infrastructure."
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {processSteps.map((step, i) => (
              <div key={step.step} style={{
                ...cardStyle,
                display: 'flex', alignItems: 'flex-start', gap: '1.5rem',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: GOLD, color: '#000', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 900, fontSize: '1rem',
                }}>
                  {step.step}
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>
                    Step {step.step}
                  </div>
                  <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', margin: '0 0 0.4rem' }}>{step.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', lineHeight: 1.6, margin: 0 }}>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Case Studies ─────────────────────────────────────────── */}
      <section style={sectionStyle}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            label="Real Results"
            title="Branding & Hosting"
            titleSpan="Success Stories"
            description="Real businesses, real results — from brand-new launches to complete rebrands and hosting migrations."
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'clamp(1rem,2vw,1.5rem)' }}>
            {caseStudies.map((cs) => (
              <div key={cs.title} style={{ ...cardStyle, display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.03)' }}>
                <div style={{
                  fontSize: '0.68rem', fontWeight: 700, color: GOLD,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  background: `${GOLD}12`, border: `1px solid ${GOLD}28`,
                  borderRadius: 9999, padding: '0.25rem 0.75rem',
                  display: 'inline-block', alignSelf: 'flex-start', marginBottom: '1rem',
                }}>
                  {cs.client}
                </div>
                <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', margin: '0 0 1rem', lineHeight: 1.35 }}>{cs.title}</h3>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem', flex: 1 }}>
                  {cs.results.map((r) => (
                    <li key={r} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
                      <span style={{ color: GOLD, fontWeight: 900, flexShrink: 0, marginTop: '0.05em' }}>✓</span> {r}
                    </li>
                  ))}
                </ul>
                <Link to="/contact-us" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                  fontSize: '0.8rem', fontWeight: 700, color: GOLD, textDecoration: 'none',
                  borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem', marginTop: 'auto',
                }}>
                  {cs.cta} <span>→</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why QuickGrowth ──────────────────────────────────────── */}
      <section style={{ ...sectionStyle, background: 'rgba(255,255,255,0.015)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            label="Why Us"
            title="Why Businesses Trust QuickGrowth for"
            titleSpan="Branding & Hosting"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'clamp(1rem,2vw,1.5rem)' }}>
            {[
              { icon: '⚡', title: 'Blazing Fast Performance', desc: 'CDN, advanced caching, and optimized servers ensure your site loads in under 2 seconds.' },
              { icon: '🛡️', title: 'Enterprise Security', desc: 'SSL, firewall, DDoS protection, and daily backups keep your site and data safe.' },
              { icon: '🎨', title: 'Original Design Only', desc: 'Every logo and brand asset is designed from scratch — no templates, no recycled concepts.' },
              { icon: '🔧', title: '24/7 Expert Support', desc: 'Our team is available around the clock for hosting issues, renewals, and brand updates.' },
              { icon: '📈', title: 'Scales With You', desc: 'Hosting plans and brand assets that grow as your business does — from startup to enterprise.' },
              { icon: '🤝', title: 'One Team, Everything', desc: 'From domain to logo to email hosting — one partner handles it all with full accountability.' },
            ].map((item) => (
              <div key={item.title} style={{ ...cardStyle }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '0.75rem' }}>{item.icon}</div>
                <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '0.95rem', margin: '0 0 0.4rem' }}>{item.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQs ─────────────────────────────────────────────────── */}
      <section style={sectionStyle}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <SectionHeading label="FAQ" title="Frequently Asked" titleSpan="Questions" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{
                ...cardStyle,
                cursor: 'pointer',
                borderColor: openFaq === i ? `${GOLD}40` : 'rgba(255,255,255,0.08)',
              }}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff', lineHeight: 1.4 }}>{faq.question}</span>
                  <span style={{ color: GOLD, fontSize: '1.2rem', flexShrink: 0 }}>{openFaq === i ? '−' : '+'}</span>
                </div>
                {openFaq === i && (
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.88rem', lineHeight: 1.7, margin: '0.75rem 0 0' }}>
                    {faq.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section style={{ ...sectionStyle, background: 'rgba(255,255,255,0.015)', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontWeight: 900, fontSize: 'clamp(1.6rem,3vw,2.2rem)', color: '#fff', margin: '0 0 1rem' }}>
            Ready to Build a Brand That <span style={{ color: GOLD }}>Stands the Test of Time?</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', lineHeight: 1.7, margin: '0 0 2rem' }}>
            Let's build your brand identity and hosting infrastructure together — from domain to launch, we handle everything.
          </p>
          <div className="bh-cta-row" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <Link to="/contact-us" className="bh-cta-btn" style={{
              background: GOLD, color: '#000', fontWeight: 700, padding: '0.9rem 2.2rem',
              borderRadius: 9999, textDecoration: 'none', fontSize: '1rem',
            }}>
              Start Your Branding Project →
            </Link>
            <Link to="/services" className="bh-cta-btn" style={{
              border: `1px solid rgba(255,255,255,0.2)`, color: '#fff', fontWeight: 600,
              padding: '0.9rem 2.2rem', borderRadius: 9999, textDecoration: 'none', fontSize: '1rem',
            }}>
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @media (max-width: 480px) {
          .bh-cta-btn {
            width: 100%;
            text-align: center;
            display: block;
            box-sizing: border-box;
          }
          .bh-cta-row {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

export default Branding;
